import axios from "axios";
import Configuration from "../shared/configuration";
import { authHeader } from "./auth-header";

/**
 * Proof of payment for methods that require evidence (Offline QR).
 *
 * Kept in its own service so nothing in invoiceservice.js has to change.
 * The photo is uploaded against the ORDER before the invoice is settled, so
 * the money path is untouched.
 */
export default class PaymentProofService {
  config = new Configuration();

  baseUrl() {
    return this.config.BASE_WEBAPI_URL + "api/PaymentProof";
  }

  /** May this order be settled by this method yet? */
  async check(splitOrderNumber, paymentMode, transactionNo) {
    try {
      const res = await axios.get(this.baseUrl() + "/Check/", {
        headers: authHeader(),
        params: {
          splitOrderNumber: splitOrderNumber,
          paymentMode: paymentMode,
          transactionNo: transactionNo || "",
        },
      });
      return res.data && res.data.data ? res.data.data : null;
    } catch (e) {
      return null;
    }
  }

  /** Photos already attached to this order. */
  async list(splitOrderNumber) {
    try {
      const res = await axios.get(this.baseUrl() + "/List/", {
        headers: authHeader(),
        params: { splitOrderNumber: splitOrderNumber },
      });
      return res.data && res.data.data ? res.data.data : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Upload one photo. Returns { ok, message }.
   * The server does the real validation (type, size, genuine image) — this only
   * carries the file up and reports back what it said.
   */
  async upload(fields, file) {
    const form = new FormData();
    form.append("SplitOrderNumber", fields.splitOrderNumber);
    form.append("PaymentMode", fields.paymentMode);
    if (fields.transactionNo) form.append("TransactionNo", fields.transactionNo);
    if (fields.amount !== undefined && fields.amount !== null)
      form.append("Amount", fields.amount);
    if (fields.remark) form.append("Remark", fields.remark);
    if (fields.storeID) form.append("StoreID", fields.storeID);
    form.append("file", file);

    try {
      const res = await axios.post(this.baseUrl() + "/Upload/", form, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      return {
        ok: res.data && res.data.statusCode === 200,
        message: res.data ? res.data.message : "Upload failed.",
      };
    } catch (e) {
      const msg =
        e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : "The photo could not be uploaded.";
      return { ok: false, message: msg };
    }
  }

  /** Authenticated URL for viewing a stored photo. */
  fileUrl(proofID) {
    return this.baseUrl() + "/File/" + proofID;
  }

  /** Fetch a photo as a blob URL — needed because the route requires the token. */
  async fileObjectUrl(proofID) {
    try {
      const res = await axios.get(this.fileUrl(proofID), {
        headers: authHeader(),
        responseType: "blob",
      });
      return URL.createObjectURL(res.data);
    } catch (e) {
      return null;
    }
  }
}
